import React, { useEffect, useState } from "react";
import { api } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { useHistory, useLocation } from "react-router-dom";

import ServiceManagementTab from "./service-management/ServiceManagementTab";
import ServiceInstancesTab from "./instances/ServiceInstancesTab";
import ServicesCalendarTab from "./calendar/ServiceCalendarTab";
import ServiceMembersTab from "./members/ServiceMembersTab";

import "../../../css/forms.css";

type ServiceMode =
  | "Manage"
  | "Instances"
  | "Calendar"
  | "Members";

interface OrganisationInfo {
  tenantId: number;
  displayName: string;
}

interface TenantAdministrationInfo {
  tenantId: number;
  name: string;
  isActive: boolean;
}

export default function ServicesPage() {
  const { isSystemAdmin, selectedTenant, user } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const initialServiceId = Number(new URLSearchParams(location.search).get("serviceId")) || null;

  const [mode, setMode] = useState<ServiceMode>("Manage");
  const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
    null,
  );
  const [selectedTenantId, setSelectedTenantId] = useState<number | null>(null);
  const [organisations, setOrganisations] = useState<OrganisationInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadOrganisations = async () => {
      try {
        const assignedOrganisations: OrganisationInfo[] =
          user?.tenants?.map((tenant) => ({
            tenantId: tenant.tenantId,
            displayName: tenant.tenantName,
          })) ?? [];

        let orgs = assignedOrganisations;

        if (isSystemAdmin) {
          const tenants = await api<TenantAdministrationInfo[]>(
            "/api/tenant-administration",
          );
          orgs = tenants
            .filter((tenant) => tenant.isActive)
            .map((tenant) => ({
              tenantId: tenant.tenantId,
              displayName: tenant.name,
            }));
        }

        if (cancelled) return;

        setOrganisations(orgs);
        setSelectedTenantId((current) => {
          const activeTenantId = selectedTenant?.tenantId;

          if (orgs.some((org) => org.tenantId === activeTenantId)) {
            return activeTenantId ?? null;
          }

          if (orgs.some((org) => org.tenantId === current)) {
            return current;
          }

          return orgs[0]?.tenantId ?? null;
        });
      } catch (error) {
        console.error("Failed to load organisations", error);

        if (!cancelled) {
          const fallbackOrganisations: OrganisationInfo[] =
            user?.tenants?.map((tenant) => ({
              tenantId: tenant.tenantId,
              displayName: tenant.tenantName,
            })) ?? [];
          setOrganisations(fallbackOrganisations);
          setSelectedTenantId(
            selectedTenant?.tenantId ??
              fallbackOrganisations[0]?.tenantId ??
              null,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void loadOrganisations();

    return () => {
      cancelled = true;
    };
  }, [isSystemAdmin, selectedTenant?.tenantId, user]);

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return <div>Loading organisations...</div>;
  }

  if (!selectedTenantId) {
    return (
      <div className="page-container">
        <div className="page-header">Services</div>
        <div className="card">
          <p>Please select an organisation to manage its services.</p>
          {organisations.length > 0 ? (
            <div className="form-group">
              <label className="form-label">Organisation</label>
              <select
                className="form-input"
                value={selectedTenantId || ""}
                onChange={(event) =>
                  setSelectedTenantId(Number(event.target.value))
                }
              >
                <option value="">Choose Organisation</option>
                {organisations.map((org) => (
                  <option key={org.tenantId} value={org.tenantId}>
                    {org.displayName}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p>No organisations available.</p>
          )}
        </div>
      </div>
    );
  }

  const backToGrid = () => {
    setSelectedServiceId(null);
    setMode("Manage");
  };

  const backToSelectedService = () => setMode("Manage");

  return (
    <div className="page-container">
      {mode === "Manage" && (
        <ServiceManagementTab
          tenantId={selectedTenantId}
          initialServiceId={selectedServiceId ?? initialServiceId}
          onManageInstances={(serviceId) => {
            setSelectedServiceId(serviceId);
            setMode("Instances");
          }}
          onViewCalendar={(serviceId) => {
            setSelectedServiceId(serviceId);
            setMode("Calendar");
          }}
          onManageMembers={(serviceId) => {
            history.push(`/services/${serviceId}/members`);
          }}
          onManageRota={(serviceId) => {
            history.push(`/services/${serviceId}/rota`, {
              rotaOrigin: "services",
            });
          }}
        />
      )}

      {mode === "Instances" && (
        <ServiceInstancesTab
          tenantId={selectedTenantId}
          serviceId={selectedServiceId}
          onServiceSelected={(serviceId) => {
            if (!serviceId) {
              setMode("Manage");
              return;
            }
            setSelectedServiceId(serviceId);
          }}
        />
      )}

      {mode === "Calendar" && (
        <ServicesCalendarTab
          tenantId={selectedTenantId}
          serviceId={selectedServiceId}
          onBack={backToSelectedService}
          onOpenInstance={(calendarServiceId, serviceInstanceId) =>
            history.push(
              `/services/${calendarServiceId}/session-reports?serviceInstanceId=${serviceInstanceId}`,
            )
          }
        />
      )}

      {mode === "Members" && (
        <ServiceMembersTab
          tenantId={selectedTenantId}
          serviceId={selectedServiceId}
          onBack={backToGrid}
        />
      )}
    </div>
  );
}
